import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GraduationCap, BookOpen, ArrowRight } from "lucide-react";
import { FOUNDATIONS_COURSE_TITLE } from "@/hooks/useFoundationsEligibility";

interface FoundationsRequiredBannerProps {
  /** Whether the user is enrolled in the course */
  isEnrolled: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Link to the Foundations course */
  courseLink: string;
  /** Compact mode for inline display */
  compact?: boolean;
}

/**
 * Banner shown to entrepreneurs who need to complete the Foundations course
 * before they can request mentorship.
 */
export function FoundationsRequiredBanner({
  isEnrolled,
  progress,
  courseLink,
  compact = false,
}: FoundationsRequiredBannerProps) {
  if (compact) {
    return (
      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Complete required training</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Finish "{FOUNDATIONS_COURSE_TITLE}" to request mentorship.
            </p>
          </div>
        </div>
        
        {isEnrolled && progress > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Your progress</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        <Button asChild size="sm" className="w-full">
          <Link to={courseLink}>
            <BookOpen className="h-4 w-4 mr-2" />
            {isEnrolled ? "Continue Course" : "Start Course"}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <Alert className="border-primary/20 bg-primary/5">
      <GraduationCap className="h-5 w-5 text-primary" />
      <AlertTitle className="text-base font-semibold">
        Complete Required Training First
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm text-muted-foreground">
          To request mentorship, please complete the{" "}
          <span className="font-medium text-foreground">
            "{FOUNDATIONS_COURSE_TITLE}"
          </span>{" "}
          course. This ensures you have the foundational knowledge to make the most of your mentorship experience.
        </p>
        
        {isEnrolled && progress > 0 && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Your progress</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        <Button asChild size="sm">
          <Link to={courseLink} className="inline-flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            {isEnrolled ? "Continue Course" : "Go to Course"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
